import { forwardRef } from 'react';
import { Award, Leaf, Star } from 'lucide-react';

const Certificate = forwardRef(function Certificate(
  { studentName, schoolName, achievement, date, type = 'monthly', onPrint },
  ref
) {
  const typeStyles = {
    monthly: {
      border: 'border-leaf-500',
      accent: 'text-leaf-600',
      bg: 'bg-leaf-50',
      ribbon: 'bg-leaf-500',
    },
    term: {
      border: 'border-sunshine-500',
      accent: 'text-sunshine-600',
      bg: 'bg-sunshine-50',
      ribbon: 'bg-sunshine-500',
    },
    annual: {
      border: 'border-purple-500',
      accent: 'text-purple-600',
      bg: 'bg-purple-50',
      ribbon: 'bg-purple-500',
    },
  };

  const style = typeStyles[type] || typeStyles.monthly;

  const handlePrint = () => {
    if (onPrint) {
      onPrint();
    } else {
      window.print();
    }
  };

  return (
    <div className="p-4">
      {/* Certificate Container - Printable Area */}
      <div
        ref={ref}
        className={`relative bg-white border-8 ${style.border} rounded-lg p-8 max-w-2xl mx-auto shadow-xl print:shadow-none`}
        style={{ aspectRatio: '1.4/1' }}
      >
        {/* Corner Decorations */}
        <div className="absolute top-4 left-4">
          <Leaf className={`${style.accent} opacity-30`} size={40} />
        </div>
        <div className="absolute top-4 right-4">
          <Leaf className={`${style.accent} opacity-30 transform scale-x-[-1]`} size={40} />
        </div>
        <div className="absolute bottom-4 left-4">
          <Leaf className={`${style.accent} opacity-30 transform scale-y-[-1]`} size={40} />
        </div>
        <div className="absolute bottom-4 right-4">
          <Leaf className={`${style.accent} opacity-30 transform scale-[-1]`} size={40} />
        </div>

        {/* Certificate Content */}
        <div className="text-center h-full flex flex-col justify-between py-4">
          {/* Header */}
          <div>
            <div className="flex justify-center mb-2">
              <div className={`w-16 h-16 rounded-full ${style.bg} flex items-center justify-center`}>
                <Award className={style.accent} size={36} />
              </div>
            </div>
            <h1 className="font-heading text-3xl text-gray-800 mb-1">
              Certificate of Achievement
            </h1>
            <p className={`text-sm ${style.accent} font-medium uppercase tracking-wider`}>
              {type === 'monthly' && 'Monthly Achievement'}
              {type === 'term' && 'Term Completion'}
              {type === 'annual' && 'Annual Excellence'}
            </p>
          </div>

          {/* Main Content */}
          <div className="py-6">
            <p className="text-gray-600 mb-2">This is to certify that</p>
            <h2 className="font-heading text-4xl text-gray-900 mb-2">{studentName}</h2>
            <p className="text-gray-600 mb-4">from {schoolName}</p>
            <div className={`inline-block px-6 py-2 rounded-full ${style.bg}`}>
              <p className={`font-semibold ${style.accent}`}>{achievement}</p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-end">
            <div className="text-left">
              <div className="w-32 border-t-2 border-gray-300 pt-2">
                <p className="text-xs text-gray-500">Teacher's Signature</p>
              </div>
            </div>
            <div className="flex flex-col items-center">
              <div className="flex gap-1 mb-2">
                {[...Array(3)].map((_, i) => (
                  <Star key={i} className="text-sunshine-400 fill-sunshine-400" size={16} />
                ))}
              </div>
              <p className="text-sm text-gray-500">
                {new Date(date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
            <div className="text-right">
              <div className="w-32 border-t-2 border-gray-300 pt-2">
                <p className="text-xs text-gray-500">Date</p>
              </div>
            </div>
          </div>
        </div>

        {/* Seal */}
        <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2">
          <div className={`w-12 h-12 rounded-full ${style.ribbon} flex items-center justify-center shadow-lg`}>
            <Leaf className="text-white" size={24} />
          </div>
        </div>
      </div>

      {/* Print Button - Hidden in Print */}
      <div className="text-center mt-8 print:hidden">
        <button
          onClick={handlePrint}
          className="btn-primary px-8 py-3 flex items-center gap-2 mx-auto"
        >
          <Award size={20} />
          Print Certificate
        </button>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #certificate-print-area,
          #certificate-print-area * {
            visibility: visible;
          }
          #certificate-print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
});

export default Certificate;
